var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { action, reaction } from 'mobx';
import { Observer } from 'mobx-react-lite';
import React from 'react';
import { useOnMount } from '../../hooks/lifecycle.hooks';
import { useCreateResizeQueryWithRef } from '../../hooks/useCreateResizeQueryWithRef.hook';
import { useProps, useStore } from '../../utils/mobx.utils';
import { countriesByDialCodes, countriesSortedByDialCodeLength, getCountryCodeFromString } from '../../utils/tel.utils';
import BaseInput from '../BaseInput/BaseInput';
import BaseLabel from '../BaseLabel/BaseLabel';
import CountryDialCodeSelector from '../CountryDialCodeSelector/CountryDialCodeSelector';
import './TelInput.scss';
var TelInput = function (props) {
    var _a = useCreateResizeQueryWithRef(), ref = _a.ref, query = _a.query;
    var p = useProps(props);
    var s = useStore(function () { return ({
        country: null,
        get countryCode() {
            var _a, _b;
            return (_b = (_a = s.country) === null || _a === void 0 ? void 0 : _a.countries[0]) === null || _b === void 0 ? void 0 : _b.cca2;
        },
        _dialCode: '',
        get dialCode() {
            var _a, _b;
            return (_b = (_a = s.country) === null || _a === void 0 ? void 0 : _a.dialCode) !== null && _b !== void 0 ? _b : '';
        },
        get number() {
            var _a, _b;
            if (!s.dialCode)
                return s.value;
            var regex = (_a = s.country) === null || _a === void 0 ? void 0 : _a.regex;
            if (!regex)
                return s.value;
            return s.value ? (_b = s.value.match(regex)) === null || _b === void 0 ? void 0 : _b[1] : s.value;
        },
        set number(v) {
            s.value = '+' + s.dialCode + (v === null || v === void 0 ? void 0 : v.replace(/\+/, ""));
        },
        get value() {
            return p.form[p.field];
        },
        set value(v) {
            Reflect.set(p.form, p.field, v);
        },
        validationResult: true,
        get validator() {
            return function (value) { return s.validationResult; };
        },
        get validators() {
            return __spreadArray([
                s.validator
            ], p.validators || [], true);
        },
        get inputStyle() {
            return { paddingLeft: query.width + 14 };
        }
    }); });
    var detectCountry = action(function () {
        var _a, _b, _c;
        var countryCode = getCountryCodeFromString(s.value);
        var def = (_a = countriesByDialCodes.find(function (c) { var _a; return ((_a = c.countries[0]) === null || _a === void 0 ? void 0 : _a.cca2) === countryCode && s.value.match(c.regex); })) !== null && _a !== void 0 ? _a : null;
        s.country = def;
        s._dialCode = (_c = (_b = s.country) === null || _b === void 0 ? void 0 : _b.dialCode) !== null && _c !== void 0 ? _c : '';
        return def;
    });
    var handleBlur = action(function () {
        if (s.countryCode === 'IE') {
            s.value = s.value.replace(/^\+3530/, '+353');
        }
    });
    var handleDialCodeManualChange = action(function (dialCode) {
        if (!dialCode)
            return;
        var def = countriesSortedByDialCodeLength.find(function (c) { var _a; return ((_a = c.countries[0]) === null || _a === void 0 ? void 0 : _a.dialCode) === dialCode; });
        if (!def)
            return;
        s.value = '+' + dialCode + s.number;
        s.country = def;
        s._dialCode = dialCode !== null && dialCode !== void 0 ? dialCode : '';
    });
    useOnMount(function () {
        return reaction(function () { return s.value; }, function () {
            detectCountry();
        }, {
            fireImmediately: true,
        });
    });
    return React.createElement(Observer, { children: function () {
            var label = p.label, form = p.form, field = p.field, rest = __rest(p, ["label", "form", "field"]);
            return React.createElement("div", { className: "TelInput", "data-country": s.countryCode, "data-field-name": p.field, "data-cy": p.dataCy },
                label && React.createElement(BaseLabel, { required: p.required }, label),
                React.createElement("div", { className: "TelInputInner" },
                    React.createElement(CountryDialCodeSelector, { form: s, field: "_dialCode", dataCy: p.dataCy + "__country-select", onUserSelection: handleDialCodeManualChange, showDialCode: true, innerRef: ref, disabled: p.disabled }),
                    React.createElement(BaseInput, __assign({}, rest, { form: s, field: "number", type: "tel", onBlur: handleBlur, dataCy: p.dataCy + "__tel-input", inputStyle: s.inputStyle, hasError: s.validationResult !== true }))));
        } });
};
export default TelInput;
